import { useEffect, useState } from 'react';

import { fetchRoute } from '@/api/endpoints/routes';
import { useTileRevision } from '@/stores/TileRevisionContext';
import type { RouteResponse } from '@/types/routes';
import { isAxios404 } from '@/utils/axiosError';

export type RouteState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'success'; readonly data: RouteResponse }
  | { readonly status: 'not_found' }
  | { readonly status: 'error'; readonly message: string };

export function useRoute(routeId: string | null): RouteState {
  const [state, setState] = useState<RouteState>({ status: 'idle' });
  const { routesRevision } = useTileRevision();

  useEffect(() => {
    if (routeId === null) {
      setState({ status: 'idle' });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    fetchRoute(routeId)
      .then((data) => {
        if (!cancelled) {
          setState({ status: 'success', data });
        }
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        if (isAxios404(error)) {
          setState({ status: 'not_found' });
          return;
        }
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : 'unknown error',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [routeId, routesRevision]);

  return state;
}
